import { useTranslation } from "react-i18next";
import Masonry from "react-masonry-css";
import { BaseAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import { QuestionViewer } from "../../SingleAssignment/componnets/questions/QuestionViewer";
import { Text } from "../../../../core";
import { useMemo } from "react";

interface CloneAssignmentPreviewProps {
  assignment: BaseAssignmentResponseDto;
}

const CloneAssignmentPreview = ({
  assignment,
}: CloneAssignmentPreviewProps) => {
  const { t } = useTranslation();

  const order = useMemo(() => ["OE", "TEST", "TF", "FB"], []);

  const questions = useMemo(
    () =>
      assignment.questions.sort((a, b) => {
        const typeOrder = order.indexOf(a.type) - order.indexOf(b.type);
        return typeOrder === 0 ? a.id - b.id : typeOrder;
      }),
    [assignment.questions, order],
  );

  const renderQuestion = (question: any, index: number) => {
    return (
      <div
        key={question.id}
        className="p-6 mb-3 bg-white dark:bg-gray-800 rounded-lg relative transform transition-all duration-300 hover:-translate-y-1 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_20px_-4px_rgba(0,0,0,0.3)] dark:hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.3)]"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-transparent dark:from-gray-800/50 rounded-lg"></div>
        <div className="relative">
          <QuestionViewer index={index + 1} question={question} />
          <div className="flex justify-end items-center gap-2 mt-4 mx-1">
            <Text
              variant="span"
              className="text-gray-600 dark:text-gray-300 font-medium text-sm"
            >
              {t("assignments.generator.preview.points", {
                points: question.point,
              })}
            </Text>
          </div>
        </div>
      </div>
    );
  };

  const breakpointColumnsObj = {
    1536: 2,
    1024: 2,
    768: 1,
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      {/* Section Title */}
      <div className="mb-6">
        <Text variant="h2" className="text-xl font-semibold mb-2">
          {t("assignments.questions")}
        </Text>
      </div>

      {/* Questions */}
      <Masonry breakpointCols={breakpointColumnsObj} className="flex gap-3">
        {questions.map((question, index) => renderQuestion(question, index))}
      </Masonry>

      {/* No Questions Message */}
      {questions.length === 0 && (
        <div className="flex justify-center items-center py-12">
          <Text variant="p" className="text-gray-500 dark:text-gray-400">
            {t("assignments.noQuestions")}
          </Text>
        </div>
      )}
    </div>
  );
};

export default CloneAssignmentPreview;
